import { ReactElement } from 'react'
import { Menu } from 'components/Menu/Menu'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import Styles from './PostStatusDropdown.module.scss'
import { Item, Section } from '@react-stately/collections'
import { Text } from 'blaze-ui/Text/Text'
import { PostStatusDropdownOption, usePostStatusDropdownState } from './usePostStatusDropdownState'
import { FloatingPortal, shift, flip, useFloating, offset } from '@floating-ui/react-dom-interactions'
import { Popover } from 'components/Popover/Popover'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { noop } from 'lodash'
import { Assignee, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import dayjs from 'dayjs'
import { Button } from 'blaze-ui/Button'
import cx from 'classnames'
import { UserIntent } from './Types'
import { ContextMenuTrigger } from 'almanac-ui/ContextMenuTrigger/ContextMenuTrigger'
import { Pressable } from '@react-aria/interactions'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { observer } from 'mobx-react-lite'

export interface PostStatusDropdownProps {
  assignee: Assignee
  size?: 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
  inDocView?: boolean
  onAction?: (intent: UserIntent) => void
  disabledKeys?: UserIntent[]
  afterExecute?: (intent: UserIntent) => void
  triggerComponent?: 'menu' | 'context-menu'
  open?: boolean
  contextMenuRef?: React.MutableRefObject<any>
  inCalendarView?: boolean
  statusOverride?: PostStatusPropertyValue
  channel?: SocialChannelType
  hasIntegrationForChannel?: boolean
}

export const buttonLabelFromIntent = (intent: UserIntent) => {
  switch (intent) {
    case UserIntent.ScheduleAutoPost:
      return 'Schedule Post'
    case UserIntent.ScheduleReminder:
      return 'Set Reminder'
    default:
      return ''
  }
}

export const PostStatusDropdown = observer(
  ({
    children,
    fullWidth = false,
    assignee,
    size = 'md',
    onAction,
    disabledKeys = [],
    afterExecute,
    triggerComponent = 'menu',
    contextMenuRef,
    inCalendarView = false,
    statusOverride,
    hasIntegrationForChannel,
  }: PostStatusDropdownProps & { children: ReactElement<any, any> }) => {
    const { isMobile } = useResponsiveDesign()
    const { state, dispatch, options, handleIntent, executeAction } = usePostStatusDropdownState({
      assignee,
      afterExecute,
      inCalendarView,
      statusOverride,
      isMobile,
    })

    const { x, y, floating, reference } = useFloating({
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [offset(4), flip(), shift({ padding: 4 })],
    })

    const handleOnAction = (intent: UserIntent) => {
      onAction?.(intent)
      handleIntent(intent)
      if (contextMenuRef) {
        contextMenuRef.current?.closeMenu()
      }
    }

    const postStatusDropdownItem = (item: PostStatusDropdownOption) => (
      <div className={cx(Styles.item, Styles[size])}>
        {item.icon}
        <div className={Styles.content}>
          <div className={Styles.labelContainer}>
            <Text variant={isMobile ? 'largeList' : 'smallList'}>{item.label}</Text>
          </div>
          {size !== 'sm' && <div className={Styles.subLabel}>{item.subLabel}</div>}
        </div>
      </div>
    )

    const menu = (
      <Menu
        aria-label="Menu"
        onAction={(action) => handleOnAction(action as UserIntent)}
        className={cx(Styles.menu, Styles[size])}
        disabledKeys={disabledKeys}
      >
        {options.map((section, sectionIndex) => (
          <Section key={sectionIndex}>
            {section.map((item) => (
              <Item key={item.action} textValue={item.action as string}>
                {postStatusDropdownItem(item)}
              </Item>
            ))}
          </Section>
        ))}
      </Menu>
    )

    const triggerContent = () => {
      switch (triggerComponent) {
        case 'menu':
          return (
            <MenuTrigger>
              {children}
              {menu}
            </MenuTrigger>
          )
        case 'context-menu':
          return (
            <ContextMenuTrigger ref={contextMenuRef}>
              <Pressable>
                <div>{children}</div>
              </Pressable>
              {() => menu}
            </ContextMenuTrigger>
          )
        default:
          return null
      }
    }

    return (
      <div ref={reference} className={cx({ [Styles.fullWidth]: fullWidth })}>
        {triggerContent()}
        {state.calendarOpen && (
          <FloatingPortal id="floating-ui-root">
            <Popover
              style={{ position: 'fixed', top: y || 0, left: x || 0 }}
              isOpen
              onClose={() => {
                dispatch({ type: 'CLOSE_CALENDAR' })
              }}
              ref={floating}
              isDismissable
            >
              <DateTimePicker
                onClose={noop}
                dateTime={state.selectedDate ? dayjs(state.selectedDate) : undefined}
                inline
                actions={
                  <div className={Styles.mobileActions}>
                    <Button onPress={() => executeAction(state.userIntent as UserIntent)}>
                      {buttonLabelFromIntent(state.userIntent as UserIntent)}
                    </Button>
                    {state.userIntent === UserIntent.ScheduleAutoPost && (
                      <Button
                        variant="tertiary"
                        onPress={() => {
                          executeAction(UserIntent.PostNow)
                        }}
                      >
                        Post Now
                      </Button>
                    )}
                  </div>
                }
                setDateTime={(datetime) => {
                  if (!datetime) return
                  dispatch({ type: 'SELECT_DATE', date: datetime.toISOString() })
                }}
              />
            </Popover>
          </FloatingPortal>
        )}
      </div>
    )
  },
)
